<template>
    <div class="h-full w-full flex flex-col p-8">

        <div class="h-full flex justify-center items-center" v-if="$apollo.queries.alumniUser.loading">
            <base-spinner></base-spinner></div>

        <div v-else class="h-full w-full flex flex-col">
            <div class="w-full flex justify-start items-center border rounded-2xl p-2 sm:p-4 relative">
                <div class="h-12 sm:h-16 lg:h-20 w-12 sm:w-16 lg:w-20 mr-2 sm:mr-4 flex-shrink-0">
                    <img class="h-full w-full object-cover rounded-xl" :src="getProfilePicture" alt="">
                </div>
                <div class="flex-grow flex flex-col">
                    <h1 class="text-base sm:text-lg lg:text-xl font-medium capitalize break-all">{{
                        alumniUser.english_name }}</h1>
                    <p class="text-xs lg:text-sm text-gray-600 break-all">{{ alumniUser.user.email }}</p>
                </div>
                <div v-if="alumniUser.is_verified_by_career_counselor" class="absolute right-0 top-0">
                    <div class="flex items-center px-4 py-2 bg-green-600 text-white rounded-tr-xl">
                        <div class="mr-1">
                            <icon-check-badge class="h-6 w-6 animate-waving-hand"></icon-check-badge>
                        </div>
                        <div class="text-sm">Verified</div>
                    </div>
                </div>
            </div>

            <div class="grid grid-col-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
                <div class="w-full flex flex-col border rounded-2xl p-4">
                    <h1 class="font-medium text-xs sm:text-sm lg:text-base">Personal Information</h1>
                    <div class="flex mt-3 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2 capitalize">English Name:</div>
                        <div class="text-gray-600">{{ alumniUser.english_name }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Arabic Name:</div>
                        <div class="text-gray-600">{{ alumniUser.arabic_name }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Emirates ID:</div>
                        <div class="text-gray-600">{{ alumniUser.emirates_id }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Nationality:</div>
                        <div class="text-gray-600">{{ alumniUser.nationality }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Gender:</div>
                        <div class="text-gray-600 capitalize">{{ alumniUser.gender }}</div>
                    </div>
                </div>
                <div class="w-full flex flex-col border rounded-2xl p-4">
                    <h1 class="font-medium text-xs sm:text-sm lg:text-base">Academic Information</h1>
                    <div class="flex mt-3 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2 capitalize">School ID:</div>
                        <div class="text-gray-600">{{ alumniUser.school_id }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Student ID:</div>
                        <div class="text-gray-600">{{ alumniUser.student_id }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Academic Year:</div>
                        <div class="text-gray-600">{{ alumniUser.academic_year }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Graduated Year:</div>
                        <div class="text-gray-600">{{ alumniUser.graduated_year }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Graduated Date:</div>
                        <div class="text-gray-600">{{ graduatedDate }}</div>
                    </div>
                </div>
                <div v-if="alumniUser.is_employed" class="w-full flex flex-col border rounded-2xl p-4">
                    <h1 class="font-medium text-xs sm:text-sm lg:text-base">Professional Information</h1>
                    <div class="flex mt-3 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2 capitalize">Company Name:</div>
                        <div class="text-gray-600">{{ alumniUser.company_name }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Job Title:</div>
                        <div class="text-gray-600">{{ alumniUser.job_title }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Industry:</div>
                        <div class="text-gray-600">{{ alumniUser.industry }}</div>
                    </div>
                    <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                        <div class="font-semibold mr-2">Year Of Experience:</div>
                        <div class="text-gray-600">{{ alumniUser.year_of_experience }}</div>
                    </div>
                </div>
                <div v-else class="w-full flex flex-col border rounded-2xl p-4">
                    <h1 class="font-medium text-xs sm:text-sm lg:text-base">Current Status</h1>
                    <div class="flex mt-3 text-xs sm:text-sm lg:text-base">
                        <div class="text-gray-600">{{ alumniUser.current_status }}</div>
                    </div>
                </div>
            </div>

            <div v-if="!alumniUser.is_verified_by_career_counselor"
                class="flex-auto w-full flex flex-col justify-end items-center mt-8">
                <div v-if="isLoading"
                    class="w-36 bg-gradient-to-r from-primary to-secondaryLight px-4 py-2 rounded-lg text-white flex items-center justify-center">
                    <base-spinner-with-text class-list="h-6 w-6 text-white" text="Submitting..."
                        text-class-list="text-white"></base-spinner-with-text>
                </div>
                <button type="button" @click.stop="submitData" v-else
                    class="w-36 bg-gradient-to-r from-primary to-secondaryLight px-4 py-2 rounded-lg text-white">Submit</button>
            </div>

        </div>

    </div>
</template>

<script>
import gql from 'graphql-tag';
import IconCheckBadge from "../../../../components/icons/IconCheckBadge.vue";
export default {
    name: "employment-users-view",
    props: ["alumniUserId"],
    components: {
        IconCheckBadge,
    },
    data() {
        return {
            isLoading: false,
            error: null,
            alumniUser: null,
        };
    },
    computed: {
        graduatedDate() {
            const parsedDate = new Date(this.alumniUser.graduated_date);
            return parsedDate.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            }).replace(/\//g, '-');
        },
        userId() {
            return this.$store.getters.userId;
        },
        getProfilePicture() {
            return process.env.VUE_APP_PUBLIC_URL + this.alumniUser.profile_picture_url;
        },
    },
    apollo: {
        alumniUser() {
            return {
                query: gql`query alumniUser($id: ID!) {
                    alumniUser(id: $id) {
                        id
                        academic_year
                        arabic_name
                        emirates_id
                        english_name
                        gender
                        graduated_date
                        graduated_year
                        nationality
                        school_id
                        student_id
                        profile_picture_url
                        is_verified_by_career_counselor
                        is_employed
                        company_name
                        job_title
                        industry
                        year_of_experience
                        current_status
                        user {
                            email
                        }
                    }
                }`,
                variables() {
                    return {
                        id: this.alumniUserId,
                    }
                },
                fetchPolicy: 'cache-and-network'
            }
        },
    },
    methods: {
        async submitData() {
            console.log('submitData')
            // this.isLoading = true;

            // const VERIFY_ALUMNI_USER_MUTATION = gql`
            //     mutation verifyAlumniUser($id: ID!, $user_id: ID!) {
            //         verifyAlumniUser(id: $id, user_id: $user_id) {
            //             error
            //             message
            //         }
            //     }
            // `;

            // try {

            //     await this.$apollo.mutate({
            //         mutation: VERIFY_ALUMNI_USER_MUTATION,
            //         variables: {
            //             id: this.alumniUser.id,
            //             user_id: this.userId
            //         },
            //     }).then((data) => {
            //         if (data.data.verifyAlumniUser.error) {
            //             this.error = data.data.verifyAlumniUser.message;
            //         } else {
            //             this.$apollo.queries.alumniUser.refetch();
            //         }
            //     }).catch((error) => {
            //         this.error = error.message;
            //     })

            // } catch (error) {
            //     this.error = error.message || "Failed to authenticate, try later.";
            // }

            // this.isLoading = false;
        },
    },
}
</script>

<style lang="scss" scoped></style>